import { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, Image, ActivityIndicator } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronRight, Wrench, CheckCircle2, StickyNote, User, Clock, Plus } from '../../lib/icons';
import { shadows } from '../../lib/styles';
import { supabase } from '../../lib/supabase';
import { getAccessibleHomeIds } from '../../lib/homeMembership';

interface Technician {
  id: string;
  name: string;
  photo_url?: string;
}

interface Visit {
  id: string;
  scheduled_at: string;
  status: 'requested' | 'proposed' | 'scheduled' | 'completed' | 'cancelled';
  visit_type: 'maintenance' | 'service' | 'walkthrough';
  home_id: string;
  maintenance_items: string[];
  service_requests: string[];
  honeydolist_items: string[];
  technician?: Technician;
  estimated_duration_hours?: number; // Supports 0.5 increments (e.g., 1, 1.5, 2, 2.5)
}

const NextVisit = () => {
  const navigate = useNavigate();
  const [nextVisit, setNextVisit] = useState<Visit | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNextVisit();
  }, []);

  const fetchNextVisit = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setLoading(false);
        return;
      }

      // Get user's homes
      const homeIds = await getAccessibleHomeIds(user.id);
      if (homeIds.length === 0) {
        setLoading(false);
        return;
      }

      // Get next scheduled, proposed, or requested visit
      const { data: visits, error: visitsError } = await supabase
        .from('visits')
        .select(`
          id,
          scheduled_date,
          status,
          visit_type,
          home_id,
          assigned_to,
          service_provider_id,
          service_providers (
            id,
            company_name,
            email,
            phone
          )
        `)
        .in('home_id', homeIds)
        .in('status', ['scheduled', 'proposed', 'requested'])
        .order('scheduled_date', { ascending: true, nullsFirst: false })
        .limit(1);

      if (visitsError) throw visitsError;

      if (!visits || visits.length === 0) {
        setNextVisit(null);
        setLoading(false);
        return;
      }

      const visit = visits[0];

      // Get visit tasks to categorize items
      const { data: visitTasks, error: tasksError } = await supabase
        .from('visit_tasks')
        .select('id, task_type, task_id, estimated_duration_minutes')
        .eq('visit_id', visit.id);

      if (tasksError) throw tasksError;

      // Categorize tasks by type
      const maintenanceItems = (visitTasks || [])
        .filter(t => t.task_type === 'maintenance_task')
        .map(t => t.task_id || t.id);
      
      const serviceRequests = (visitTasks || [])
        .filter(t => t.task_type === 'service_request')
        .map(t => t.task_id || t.id);
      
      const honeydoItems = (visitTasks || [])
        .filter(t => t.task_type === 'user_task')
        .map(t => t.task_id || t.id);

      // Calculate total estimated duration
      const totalMinutes = (visitTasks || [])
        .reduce((sum, t) => sum + (t.estimated_duration_minutes || 0), 0);
      const estimatedHours = totalMinutes > 0 ? totalMinutes / 60 : undefined;

      // Determine technician info
      let technician: Technician | undefined;
      if (visit.service_providers && Array.isArray(visit.service_providers) && visit.service_providers.length > 0) {
        const sp = visit.service_providers[0];
        technician = {
          id: sp.id,
          name: sp.company_name,
          photo_url: undefined, // Service providers don't have photos in current schema
        };
      } else if (visit.assigned_to) {
        // For walkthrough visits, fetch the home manager's profile photo
        if (visit.visit_type === 'walkthrough') {
          const { data: managerProfile } = await supabase
            .from('profiles')
            .select('id, full_name, profile_photo_url')
            .eq('full_name', visit.assigned_to)
            .eq('is_system_manager', true)
            .maybeSingle();
          
          if (managerProfile) {
            technician = {
              id: managerProfile.id,
              name: managerProfile.full_name,
              photo_url: managerProfile.profile_photo_url || undefined,
            };
          } else {
            // Fallback if profile not found
            technician = {
              id: 'internal',
              name: visit.assigned_to,
              photo_url: undefined,
            };
          }
        } else {
          technician = {
            id: 'internal',
            name: visit.assigned_to,
            photo_url: undefined,
          };
        }
      }

      setNextVisit({
        id: visit.id,
        scheduled_at: visit.scheduled_date || '',
        status: visit.status as 'scheduled' | 'completed' | 'cancelled' | 'requested',
        visit_type: visit.visit_type as 'maintenance' | 'service' | 'walkthrough',
        home_id: visit.home_id,
        maintenance_items: maintenanceItems,
        service_requests: serviceRequests,
        honeydolist_items: honeydoItems,
        technician,
        estimated_duration_hours: estimatedHours,
      });
    } catch (error) {
      console.error('Error fetching next visit:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatVisitDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDuration = (hours: number) => {
    if (hours === 1) {
      return '1 hour';
    } else if (hours < 1) {
      return `${hours * 60} minutes`;
    } else if (hours % 1 === 0) {
      return `${hours} hours`;
    } else {
      return `${hours} hours`;
    }
  };

  const getTotalItems = (visit: Visit) => {
    return visit.maintenance_items.length + visit.service_requests.length + visit.honeydolist_items.length;
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Calendar size={24} color="#111827" />
            <Text style={styles.title}>Upcoming Visit</Text>
          </View>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color="#9ca3af" />
        </View>
      </View>
    );
  }

  if (!nextVisit) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Calendar size={24} color="#111827" />
            <Text style={styles.title}>Upcoming Visit</Text>
          </View>
        </View>

        <View style={styles.noVisitContainer}>
          <Calendar size={48} color="#9ca3af" />
          <View style={styles.noVisitTextContainer}>
            <Text style={styles.noVisitTitle}>No Visit Scheduled</Text>
            <Text style={styles.noVisitDescription}>
              Start planning your next visit. Select maintenance items, service requests, and HoneyDo tasks you'd like us to handle.
            </Text>
          </View>
          <Pressable
            style={({ pressed }) => [
              styles.requestButton,
              pressed && styles.requestButtonPressed,
            ]}
            onPress={() => navigate('/visit-plan')}
          >
            <Calendar size={16} color="#ffffff" />
            <Text style={styles.requestButtonText}>
              Plan a Visit
            </Text>
          </Pressable>
        </View>
      </View>
    );
  }

  const isWalkthroughVisit = nextVisit.visit_type === 'walkthrough';

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Calendar size={24} color="#111827" />
          <Text style={styles.title}>Upcoming Visit</Text>
        </View>
        {!isWalkthroughVisit && (
          <Pressable
            onPress={() => navigate('/visit-plan', { state: { visitId: nextVisit.id } })}
            style={styles.planButton}
          >
            <Text style={styles.planButtonText}>
              {(nextVisit.status === 'requested' || nextVisit.status === 'proposed') ? 'Edit' : 'Plan Visit'}
            </Text>
            <ChevronRight size={16} color="#2563eb" />
          </Pressable>
        )}
      </View>

      {isWalkthroughVisit ? (
        <View style={styles.visitCard}>
          <View style={styles.walkthroughHeader}>
            <Text style={styles.walkthroughTitle}>Walkthrough</Text>
            {nextVisit.scheduled_at ? (
              <Text style={styles.walkthroughSubtitle}>
                {formatVisitDate(nextVisit.scheduled_at)}
              </Text>
            ) : (
              <Text style={styles.walkthroughSubtitle}>Date TBD</Text>
            )}
            <View style={[
              styles.scheduledBadge,
              (nextVisit.status === 'requested' || nextVisit.status === 'proposed') && styles.requestedBadge
            ]}>
              <Text style={[
                styles.scheduledBadgeText,
                (nextVisit.status === 'requested' || nextVisit.status === 'proposed') && styles.requestedBadgeText
              ]}>
                {nextVisit.status === 'requested' ? 'Pending' : nextVisit.status === 'proposed' ? 'Proposed' : 'Scheduled'}
              </Text>
            </View>
          </View>

        {(nextVisit.status === 'requested' || nextVisit.status === 'proposed') && (
          <View style={styles.requestNotice}>
            <Text style={styles.requestNoticeText}>
              We're reviewing your request and will follow up to confirm details and schedule your visit.
            </Text>
          </View>
        )}

        {nextVisit.estimated_duration_hours && nextVisit.scheduled_at && (
          <View style={styles.durationContainer}>
            <Clock size={16} color="#6b7280" />
            <Text style={styles.durationText}>
              Estimated Duration: {formatDuration(nextVisit.estimated_duration_hours)}
            </Text>
          </View>
        )}

        {nextVisit.technician && (
          <View style={styles.technicianSection}>
            <View style={styles.technicianContent}>
              {nextVisit.technician.photo_url ? (
                <Image
                  source={{ uri: nextVisit.technician.photo_url }}
                  style={styles.technicianPhoto}
                />
              ) : (
                <View style={styles.technicianPhotoPlaceholder}>
                  <User size={24} color="#9ca3af" />
                </View>
              )}
              <View style={styles.technicianInfo}>
                <Text style={styles.technicianLabel}>
                  {isWalkthroughVisit ? 'Home Manager' : 'Your Technician'}
                </Text>
                <Text style={styles.technicianName}>{nextVisit.technician.name}</Text>
              </View>
            </View>
          </View>
        )}
        </View>
      ) : (
        <Pressable
          onPress={() => navigate('/visit-plan', { state: { visitId: nextVisit.id } })}
          style={({ pressed }) => [
            styles.visitCard,
            pressed && styles.visitCardPressed,
          ]}
        >
          <View style={styles.visitHeader}>
            <View style={styles.visitDateContainer}>
              <Calendar size={20} color="#2563eb" />
              {nextVisit.scheduled_at ? (
                <Text style={styles.visitDate}>
                  {formatVisitDate(nextVisit.scheduled_at)}
                </Text>
              ) : (
                <Text style={styles.visitDate}>Date TBD</Text>
              )}
            </View>
            <View style={[
              styles.scheduledBadge,
              (nextVisit.status === 'requested' || nextVisit.status === 'proposed') && styles.requestedBadge
            ]}>
              <Text style={[
                styles.scheduledBadgeText,
                (nextVisit.status === 'requested' || nextVisit.status === 'proposed') && styles.requestedBadgeText
              ]}>
                {nextVisit.status === 'requested' ? 'Pending' : nextVisit.status === 'proposed' ? 'Proposed' : 'Scheduled'}
              </Text>
            </View>
          </View>

          {(nextVisit.status === 'requested' || nextVisit.status === 'proposed') && (
            <View style={styles.requestNotice}>
              <Text style={styles.requestNoticeText}>
                We're reviewing your request and will follow up to confirm details and schedule your visit.
              </Text>
            </View>
          )}

          {nextVisit.estimated_duration_hours && nextVisit.scheduled_at && (
            <View style={styles.durationContainer}>
              <Clock size={16} color="#6b7280" />
              <Text style={styles.durationText}>
                Estimated Duration: {formatDuration(nextVisit.estimated_duration_hours)}
              </Text>
            </View>
          )}

          {nextVisit.technician && (
            <View style={styles.technicianSection}>
              <View style={styles.technicianContent}>
                {nextVisit.technician.photo_url ? (
                  <Image
                    source={{ uri: nextVisit.technician.photo_url }}
                    style={styles.technicianPhoto}
                  />
                ) : (
                  <View style={styles.technicianPhotoPlaceholder}>
                    <User size={24} color="#9ca3af" />
                  </View>
                )}
                <View style={styles.technicianInfo}>
                  <Text style={styles.technicianLabel}>Your Technician</Text>
                  <Text style={styles.technicianName}>{nextVisit.technician.name}</Text>
                </View>
              </View>
            </View>
          )}

          <View style={styles.visitSummary}>
            <Text style={styles.summaryTitle}>
              {getTotalItems(nextVisit)} {getTotalItems(nextVisit) === 1 ? 'item' : 'items'} planned
            </Text>
            <View style={styles.summaryItems}>
              {nextVisit.maintenance_items.length > 0 && (
                <View style={styles.summaryItem}>
                  <CheckCircle2 size={16} color="#16a34a" />
                  <Text style={styles.summaryItemText}>
                    {nextVisit.maintenance_items.length} Maintenance
                  </Text>
                </View>
              )}
              {nextVisit.service_requests.length > 0 && (
                <View style={styles.summaryItem}>
                  <Wrench size={16} color="#ea580c" />
                  <Text style={styles.summaryItemText}>
                    {nextVisit.service_requests.length} Service {nextVisit.service_requests.length === 1 ? 'Request' : 'Requests'}
                  </Text>
                </View>
              )}
              {nextVisit.honeydolist_items.length > 0 && (
                <View style={styles.summaryItem}>
                  <StickyNote size={16} color="#8b5cf6" />
                  <Text style={styles.summaryItemText}>
                    {nextVisit.honeydolist_items.length} HoneyDo {nextVisit.honeydolist_items.length === 1 ? 'Item' : 'Items'}
                  </Text>
                </View>
              )}
            </View>
          </View>

          <View style={styles.visitFooter}>
            <Text style={styles.footerText}>Tap to plan this visit</Text>
            <ChevronRight size={20} color="#9ca3af" />
          </View>
        </Pressable>
      )}

      {/* Request Another Visit Button */}
      <Pressable
        onPress={() => navigate('/visit-plan', { state: { createNew: true } })}
        style={({ pressed }) => [
          styles.requestAnotherButton,
          pressed && styles.requestAnotherButtonPressed,
        ]}
      >
        <Plus size={16} color="#2563eb" />
        <Text style={styles.requestAnotherButtonText}>Request Another Visit</Text>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 24,
    gap: 20,
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  planButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  planButtonText: {
    fontSize: 14,
    color: '#2563eb',
    fontWeight: '500',
  },
  noVisitContainer: {
    paddingVertical: 32,
    paddingHorizontal: 16,
    alignItems: 'center',
    gap: 16,
  },
  noVisitTextContainer: {
    alignItems: 'center',
    gap: 8,
  },
  noVisitTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  noVisitDescription: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
  },
  requestButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  requestButtonPressed: {
    backgroundColor: '#1f2937',
  },
  requestButtonText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '600',
  },
  visitCard: {
    backgroundColor: '#fafafa',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
    gap: 16,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.2s',
    }),
  },
  visitCardPressed: {
    backgroundColor: '#f3f4f6',
  },
  walkthroughHeader: {
    gap: 8,
  },
  walkthroughTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  walkthroughSubtitle: {
    fontSize: 16,
    color: '#6b7280',
    fontWeight: '500',
  },
  visitHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
  },
  visitDateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  visitDate: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
  },
  scheduledBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    backgroundColor: '#eff6ff',
    borderRadius: 12,
  },
  scheduledBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#2563eb',
  },
  requestedBadge: {
    backgroundColor: '#faf5ff',
  },
  requestedBadgeText: {
    color: '#8b5cf6',
  },
  requestNotice: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: '#faf5ff',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e9d5ff',
  },
  requestNoticeText: {
    fontSize: 13,
    color: '#6b21a8',
    lineHeight: 18,
  },
  durationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingTop: 8,
  },
  durationText: {
    fontSize: 14,
    color: '#6b7280',
    fontWeight: '500',
  },
  loadingContainer: {
    padding: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  technicianSection: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
  },
  technicianContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  technicianPhoto: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#e5e7eb',
  },
  technicianPhotoPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#f3f4f6',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  technicianInfo: {
    flex: 1,
    gap: 2,
  },
  technicianLabel: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  technicianName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  visitSummary: {
    gap: 12,
  },
  summaryTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  summaryItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  summaryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  summaryItemText: {
    fontSize: 14,
    color: '#6b7280',
  },
  visitFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  footerText: {
    fontSize: 13,
    color: '#9ca3af',
  },
  requestAnotherButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#2563eb',
    marginTop: 12,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.2s',
    }),
  },
  requestAnotherButtonPressed: {
    backgroundColor: '#eff6ff',
  },
  requestAnotherButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2563eb',
  },
});

export default NextVisit;


